import React, { useEffect, useState, useRef, useCallback } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { TextField, Button } from "@mhome/ui";
import { formatWithPrecision } from "../common/number-helper";
const SLIDER_RADIUS = 16;

const NumberInput = ({ valueSpec, onChange, onError, disabled }) => {
  const [sValue, setSValue] = useState(null);
  const [tValue, setTValue] = useState("");
  const [localValue, setLocalValue] = useState(null);
  const isReadOnly = valueSpec.readOnly;
  const [hideButtons, setHideButtons] = useState(false);
  const [outOfRange, setOutOfRange] = useState(false);
  const containerRef = useRef(null);
  const sliderContainerRef = useRef(null);
  const dragging = useRef(false);
  const touchStartRef = useRef(null);
  const isScrolling = useRef(false);
  const valueToCommit = useRef(null);

  const step = valueSpec?.step || 1;
  const precision = valueSpec?.precision || 1;

  // Use localValue during dragging for immediate visual feedback
  const displayValue = dragging.current ? localValue : sValue;
  const { min, max } = valueSpec;
  const percent =
    min !== null && max !== null
      ? Math.max(0, Math.min(1, (displayValue - min) / (max - min)))
      : 0;

  useEffect(() => {
    const value = Number(valueSpec.value);
    setSValue(value);
    setTValue(value);
    setLocalValue(value);
    valueToCommit.current = value;
    const valid = verifyValue(value, valueSpec.min, valueSpec.max);
    if (!valid) {
      if (onError) {
        onError();
      }
    }
  }, [valueSpec]);

  // Sync localValue with sValue when not dragging
  useEffect(() => {
    if (!dragging.current) {
      setLocalValue(sValue);
    }
  }, [sValue]);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const width = entry.contentRect.width;
        // Hide buttons if width is less than 200px (adjust threshold as needed)
        setHideButtons(width < 160);
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const verifyValue = (value, minBound, maxBound) => {
    const isOutOfRange =
      (minBound !== null && minBound !== undefined && value < minBound) ||
      (maxBound !== null && maxBound !== undefined && value > maxBound);
    setOutOfRange(isOutOfRange);
    return !isOutOfRange;
  };

  const handleMove = useCallback(
    (clientX) => {
      if (!sliderContainerRef.current) return;
      const rect = sliderContainerRef.current.getBoundingClientRect();
      const { min, max } = valueSpec;
      const step = valueSpec.step || 1;

      let x = clientX - rect.left;
      x = Math.max(0, Math.min(rect.width, x));
      if (rect.width === 0) return;

      const newPercent = x / rect.width;
      let newValue = newPercent * (max - min) + min;

      // Snap to step
      newValue = Math.round(newValue / step) * step;

      // Clamp to min/max
      newValue = Math.max(min, Math.min(max, newValue));

      // Use batched updates to reduce re-renders during dragging
      unstable_batchedUpdates(() => {
        setLocalValue(newValue); // Update local state immediately for visual feedback
        setSValue(newValue);
        setTValue(newValue);
      });
      valueToCommit.current = newValue;
      verifyValue(newValue, valueSpec.min, valueSpec.max);
    },
    [valueSpec]
  );

  const commitChange = useCallback(
    (value) => {
      console.debug("commitChange", value);
      const valid = verifyValue(value, valueSpec.min, valueSpec.max);
      if (!valid) {
        if (onError) {
          onError();
        }
      } else {
        onChange(formatWithPrecision(value, precision));
      }
    },
    [valueSpec, onChange, onError]
  );

  const handleMouseDown = (e) => {
    if (disabled || isReadOnly) return;
    e.preventDefault();
    dragging.current = true;
    handleMove(e.clientX);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!dragging.current) return;
      handleMove(e.clientX);
    },
    [handleMove]
  );

  const handleMouseUp = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;
    commitChange(valueToCommit.current);
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  }, [handleMouseMove, commitChange]);

  const handleTouchStart = (e) => {
    if (disabled || isReadOnly) return;
    dragging.current = false;
    isScrolling.current = false;
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
  };

  const handleTouchMove = useCallback(
    (e) => {
      if (isScrolling.current || !touchStartRef.current) return;

      if (dragging.current) {
        e.preventDefault();
        handleMove(e.touches[0].clientX);
        return;
      }

      const deltaX = Math.abs(e.touches[0].clientX - touchStartRef.current.x);
      const deltaY = Math.abs(e.touches[0].clientY - touchStartRef.current.y);
      const moveThreshold = 5;

      if (deltaX > moveThreshold || deltaY > moveThreshold) {
        if (deltaY > deltaX) {
          isScrolling.current = true;
          touchStartRef.current = null;
          window.removeEventListener("touchmove", handleTouchMove);
          window.removeEventListener("touchend", handleTouchEnd);
        } else {
          e.preventDefault();
          dragging.current = true;
          handleMove(e.touches[0].clientX);
        }
      }
    },
    [handleMove]
  );

  const handleTouchEnd = useCallback(
    (e) => {
      if (isScrolling.current) {
        isScrolling.current = false;
        touchStartRef.current = null;
        return;
      }

      if (dragging.current) {
        commitChange(valueToCommit.current);
      } else {
        // Tap event
        handleMove(e.changedTouches[0].clientX);
        commitChange(valueToCommit.current);
      }

      dragging.current = false;
      isScrolling.current = false;
      touchStartRef.current = null;
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    },
    [handleMove, handleTouchMove, commitChange]
  );

  const handleNumberInputChange = (event) => {
    if (disabled) return;
    if (event.target.value === "") {
      setSValue(0);
      setTValue("");
    } else {
      const newValue = Number(event.target.value);
      setSValue(newValue);
      setTValue(newValue);
    }
    const newValue = Number(event.target.value);
    verifyValue(newValue, valueSpec.min, valueSpec.max);
  };

  const processInputValue = (value) => {
    if (value === "") {
      setSValue(0);
      setTValue(0);
      const valid = verifyValue(0, valueSpec.min, valueSpec.max);
      if (!valid) {
        if (onError) {
          onError();
        }
      } else {
        onChange("0");
      }
    } else {
      const newValue = Number(value);
      setSValue(newValue);
      setTValue(newValue);
      const valid = verifyValue(newValue, valueSpec.min, valueSpec.max);
      if (!valid) {
        if (onError) {
          onError();
        }
      } else {
        onChange(formatWithPrecision(newValue, precision));
      }
    }
  };

  const handleNumberInputBlur = (event) => {
    if (disabled) return;
    processInputValue(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === "Enter") {
      event.preventDefault();
      processInputValue(event.target.value);
      event.target.blur(); // Remove focus from the input
    }
  };

  const handleIncrement = () => {
    if (disabled) return;
    const newValue = sValue + step;
    setSValue(newValue);
    setTValue(newValue);
    const valid = verifyValue(newValue, valueSpec.min, valueSpec.max);
    if (!valid) {
      if (onError) {
        onError();
      }
    } else {
      onChange(formatWithPrecision(newValue, precision));
    }
  };

  const handleDecrement = () => {
    const newValue = sValue - step;
    setSValue(newValue);
    setTValue(newValue);
    const valid = verifyValue(newValue, valueSpec.min, valueSpec.max);
    if (!valid) {
      if (onError) {
        onError();
      }
    } else {
      onChange(formatWithPrecision(newValue, precision));
    }
  };

  const hasSlider =
    valueSpec.max !== null &&
    valueSpec.max !== undefined &&
    valueSpec.min !== null &&
    valueSpec.min !== undefined;

  return (
    <div
      ref={containerRef}
      className="w-full h-full flex items-center justify-center"
    >
      <div className="w-full h-full flex items-center flex-1 gap-2">
        {hasSlider ? (
          <>
            <div
              ref={sliderContainerRef}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              className="relative flex-1 h-3 rounded-2xl overflow-visible select-none"
              style={{
                background: "hsl(var(--muted))",
                cursor: disabled || isReadOnly ? "default" : "pointer",
                opacity: disabled ? 0.8 : 1,
              }}
            >
              {/* Colored track */}
              <div
                className="absolute left-0 top-0 h-full rounded-2xl pointer-events-none"
                style={{
                  width: `${percent * 100}%`,
                  background: "hsl(var(--primary))",
                  transition: dragging.current ? "none" : "width 0.2s",
                  zIndex: 1,
                }}
              />
            </div>
            <TextField
              type="number"
              value={formatWithPrecision(tValue, precision)}
              onChange={handleNumberInputChange}
              onBlur={handleNumberInputBlur}
              onKeyDown={handleKeyDown}
              className="min-h-[28px] text-center"
              style={{
                width: "auto",
                maxWidth: hasSlider ? "150px" : "auto",
                minHeight: "28px",
              }}
              InputProps={{
                style: {
                  minHeight: "28px",
                  padding: "0 8px",
                  textAlign: "center",
                },
                readOnly: isReadOnly,
              }}
              error={outOfRange}
            />
          </>
        ) : (
          <div className="flex items-center justify-center gap-2 w-full min-h-[28px]">
            {!hideButtons && (
              <Button
                onClick={handleDecrement}
                variant="outlined"
                disabled={disabled || isReadOnly}
                className="min-w-[28px] w-[28px] h-[28px] min-h-[28px] p-0 text-base font-semibold"
                style={{
                  opacity: isReadOnly ? 0.7 : 1,
                  cursor: isReadOnly ? "default" : "pointer",
                }}
              >
                -
              </Button>
            )}
            <TextField
              type="number"
              value={formatWithPrecision(tValue, precision)}
              onChange={handleNumberInputChange}
              onBlur={handleNumberInputBlur}
              onKeyDown={handleKeyDown}
              className="min-h-[28px] text-center"
              style={{
                minHeight: "28px",
              }}
              InputProps={{
                style: {
                  minHeight: "28px",
                  padding: "0 8px",
                  textAlign: "center",
                },
                readOnly: isReadOnly,
              }}
              error={outOfRange}
            />
            {!hideButtons && (
              <Button
                onClick={handleIncrement}
                disabled={disabled || isReadOnly}
                variant="outlined"
                className="min-w-[28px] w-[28px] h-[28px] min-h-[28px] p-0 text-base font-semibold"
                style={{
                  opacity: isReadOnly ? 0.7 : 1,
                  cursor: isReadOnly ? "default" : "pointer",
                }}
              >
                +
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NumberInput;
